/**
 * Host-facing callback payload for the train-results widget (`TRAIN_WIDGET_DONE`).
 * Mirrors the contract expected by the parent page: stable cart id, booking id, status,
 * trip + journey, accommodation, travellers, contact, price, and optional payment (paid flow).
 */
import store from '@/store/BookingStore'
import type { Booking, Traveller } from '../../../../requests-module/src'
import { PaymentStatusEnum, StatusV2 } from '../../../../requests-module/src'
import type { PaymentApiResponse } from '@/logic/payment/stripe/v2/OrchestratorService'
import {
    getNightTrainOptions,
    isCompartimentOption,
    isCouchetteOption,
    isTrainOption,
} from '@/logic/options/getNightTrainOptions'

export interface WidgetHostCallbackPayload {
    uuid: string
    bookingId: string | null
    status: 'cart' | 'paid'
    trip: 'one-way' | 'round-trip'
    journey: {
        origin: string | null
        destination: string | null
        outbound: string | null
        inbound: string | null
    }
    accommodation: 'cabin' | 'berth'
    passengers: Array<{
        title: string
        firstname: string
        lastname: string
        birthdate: string
        fareCategory: string | null
    }>
    customer: {
        email: string
        phone: string
        title: string
        firstname: string
        lastname: string
        birthdate: string | null
        [extra: string]: unknown
    }
    price: {
        total: number | null
        currency: string
        breakdown: Array<Record<string, unknown>>
    }
    payment?: {
        provider: string
        pspReference: string
        method: string
    }
}

/** @deprecated Use WidgetHostCallbackPayload */
export type WidgetCallbackPayload = WidgetHostCallbackPayload

export type WidgetCallbackBuildExtra = Partial<WidgetHostCallbackPayload> & {
    /** Legacy: map orchestrator payment status when finishing payment in widget iframe. */
    paymentStatus?: StatusV2
}

function toYyyyMmDd(s: string | null | undefined): string | null {
    if (!s || typeof s !== 'string') return null
    const d = s.trim().slice(0, 10)
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null
}

function tripFromBooking(booking: Booking | null): 'one-way' | 'round-trip' {
    const r = booking?.returnDate?.trim()
    if (!r) return 'one-way'
    return 'round-trip'
}

function genderToTitle(g: string | null | undefined): string {
    if (!g) return ''
    const u = g.toUpperCase()
    if (u === 'MALE' || u === 'M') return 'Mr'
    if (u === 'FEMALE' || u === 'F') return 'Mrs'
    return ''
}

function deriveAccommodation(booking: Booking | null): 'cabin' | 'berth' {
    if (!booking?.options?.length) return 'cabin'
    const trainOpts = booking.options.filter(isTrainOption)
    const active = trainOpts.filter((o) => (o.quantity ?? 0) > 0)
    for (const o of active) {
        if (isCompartimentOption(o)) return 'cabin'
    }
    for (const o of active) {
        if (isCouchetteOption(o)) return 'berth'
    }
    const n = getNightTrainOptions(booking)
    if (n.compartiment && (n.compartiment.quantity ?? 0) > 0) return 'cabin'
    if (n.couchette && (n.couchette.quantity ?? 0) > 0) return 'berth'
    return 'cabin'
}

function mapPassenger(tr: Traveller): WidgetHostCallbackPayload['passengers'][0] {
    return {
        title: genderToTitle(tr.gender ?? undefined),
        firstname: tr.firstName?.trim() ?? '',
        lastname: tr.lastName?.trim() ?? '',
        birthdate: tr.birthday?.trim() ?? '',
        fareCategory: tr.type ?? null,
    }
}

function buildPhone(phoneCode: string | undefined, phoneNumber: string | undefined): string {
    const c = (phoneCode || '').replace(/^\+?/, '')
    const n = phoneNumber || ''
    if (!c && !n) return ''
    return c ? `+${c}${n}` : n
}

function inferPaymentProvider(init: PaymentApiResponse | null): string {
    const opts = init?.paymentOptions ?? []
    for (const o of opts) {
        const key = (o.key || '').toLowerCase()
        const method = (o.method || '').toLowerCase()
        if (key.includes('adyen') || method.includes('adyen')) return 'adyen'
        if (key.includes('stripe') || method.includes('stripe')) return 'stripe'
        if (key.includes('systempay') || method.includes('systempay')) return 'systempay'
    }
    return 'unknown'
}

function buildPriceBreakdown(booking: Booking | null, init: PaymentApiResponse | null): WidgetHostCallbackPayload['price'] {
    const currency = init?.payments?.[0]?.currency ?? 'EUR'
    const payments = init?.payments ?? []
    const breakdown: Array<Record<string, unknown>> = payments.map((p) => ({
        paymentId: p.paymentId,
        orderId: p.orderId,
        status: p.status,
        amount: typeof p.amount === 'number' ? p.amount / 100 : null,
        currency: p.currency,
        date: p.date,
    }))

    const total =
        booking?.sellingPrice ??
        (typeof init?.totalPrice === 'number' ? init.totalPrice / 100 : null)

    return {
        total,
        currency,
        breakdown,
    }
}

function resolveStatus(overrides: Partial<WidgetHostCallbackPayload>, paymentStatus: StatusV2 | undefined): 'cart' | 'paid' {
    if (overrides.status) return overrides.status
    if (paymentStatus === StatusV2.SUCCESS) return 'paid'
    return 'cart'
}

function buildPaymentSlice(
    booking: Booking | null,
    init: PaymentApiResponse | null,
    status: 'cart' | 'paid'
): WidgetHostCallbackPayload['payment'] | undefined {
    if (status !== 'paid') return undefined
    const token = store.getters.getToken as string | null
    const ref = booking?.reference ?? token ?? ''
    const payments = init?.payments ?? []
    const paidLike =
        payments.find((p) => p.status === PaymentStatusEnum.PAID || p.status === PaymentStatusEnum.UNPAID) ??
        payments[0]
    return {
        provider: inferPaymentProvider(init),
        pspReference: paidLike?.paymentId ?? ref,
        method: 'card',
    }
}

export const buildWidgetPayload = (extra: WidgetCallbackBuildExtra = {}): WidgetHostCallbackPayload => {
    const { paymentStatus, ...overrides } = extra

    const meta = store.getters.getWidgetMeta
    const booking = store.getters.getBooking as Booking | null
    const registerForm = store.getters.getRegisterForm
    const loggedUser = store.getters.getLoggedUser
    const init = store.getters.getInitPaymentResponse as PaymentApiResponse | null
    const token = store.getters.getToken as string | null

    const travellers = booking?.travellers ?? []
    const passengers = travellers.map(mapPassenger)

    const email =
        (loggedUser?.email as string) ||
        registerForm?.email ||
        (store.getters.getEmail as string) ||
        ''

    const firstname = (loggedUser?.first_name as string) || registerForm?.first_name || ''
    const lastname = (loggedUser?.last_name as string) || registerForm?.last_name || ''
    const genderRaw = (loggedUser?.gender as string) || String(registerForm?.gender ?? '')

    const customer: WidgetHostCallbackPayload['customer'] = {
        email,
        phone: buildPhone(registerForm?.phone_code, registerForm?.phone_number),
        title: genderToTitle(genderRaw),
        firstname,
        lastname,
        birthdate: null,
    }

    const status = resolveStatus(overrides, paymentStatus)

    const base: WidgetHostCallbackPayload = {
        uuid: meta.uuid,
        bookingId: booking?.reference ?? token ?? null,
        status,
        trip: tripFromBooking(booking),
        journey: {
            origin: booking?.origin ?? null,
            destination: booking?.destination ?? null,
            outbound: toYyyyMmDd(booking?.departureDate),
            inbound: toYyyyMmDd(booking?.returnDate),
        },
        accommodation: deriveAccommodation(booking),
        passengers,
        customer,
        price: buildPriceBreakdown(booking, init),
        payment: buildPaymentSlice(booking, init, status),
    }

    return {
        ...base,
        ...overrides,
        customer: { ...base.customer, ...(overrides.customer ?? {}) },
    }
}
